import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Toggle } from '@uhg-abyss/web/ui/Toggle';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { find } from 'lodash';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import translationsEnJson from '../../../../../public/locale/transperfect/en/translation.json';
import { FeatureFlags } from '../../../../common/ConstantsFeatureFlags';
import { PortalContext } from '../../../../context/PortalContext';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useGetTokenOrUrlParams } from '../../../../hooks/useInitializeStore';
import { useLagoon } from '../../../../hooks/useLagoon';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { useSuppressNiceMembers } from '../../../../hooks/useSuppressNiceMembers';
import { CustomPreferredPolicy } from '../../../../models/Lagoon';
import {
  ProviderFiltersType,
  ProviderSearchFilters,
} from '../../../../models/ProviderSearch';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import { returnSuppressFlag } from '../../../../utils/featureSuppress';
import { portalShouldShowVirtualVisits } from '../../../../utils/portalConfig.utils';
import {
  getLagoonConfigValue,
  parseLagoonConfigArray,
} from '../../../../utils/providerSearch.utils';
import { removeLeadingZeroes } from '../../../../utils/string.utils';
import {
  getCurrentMember,
  isMNRMember,
  isMemberEAPEligible,
  isNotMNRMember,
  validateMnrTieredBenefitEligibility,
} from '../../../../utils/user.utils';
import { adobeLinkTrackEvent } from '../../../AdobeTagging/adobeLinkTrackEvent';
import { CheckBoxPopover } from '../../../CheckBoxGroup/CheckBoxInfoPopover';
import {
  Constants,
  EAP_LABEL,
  EAP_LABEL_ES,
  EAP_OPTION_LABEL,
  EAP_OPTION_LABEL_ES,
  FilterCollectionModel,
  ReverseCoverageTypesCodes,
} from '../../../Constants';
import { ConstantsLagoon } from '../../../ConstantsLagoon';
import { isCnsMember } from '../../../Utils/memberUtils/memberUtils';
import {
  applyBccAutoSelectLogic,
  isPrioritySchedulingActive,
} from '../../BccFilterUtils';

const { PROVIDER, PROVIDER_GROUPS, FACILITY } = Constants.RESULT_SECTION;
const BEHAVIORAL_HEALTH = ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'];
const { MEDICAL, VISION } = ReverseCoverageTypesCodes;

const eapPresent = (selectedFilters) =>
  (selectedFilters?.EapOffered || []).findIndex(
    (option) =>
      option?.dnt_label === EAP_OPTION_LABEL ||
      option?.dnt_label === EAP_OPTION_LABEL_ES
  ) !== -1;

const getEAPOptionDetails = (providerFilters) => {
  const filter = providerFilters.find(
    (filter) => filter.model === FilterCollectionModel.EAP_OFFERED
  );
  return filter?.options?.find(
    (option) => option?.dnt_label === EAP_OPTION_LABEL
  );
};

interface Props {
  sectionType: string | undefined;
  selectedFilters: Omit<
    ProviderSearchFilters,
    'PageNumber' | 'AutoSearchRadius'
  >;
  setSelectedFilters: (
    data: ProviderSearchFilters,
    lastModifiedModel?: string
  ) => void;
  providerFilters?: ProviderFiltersType[];
  suppressPremiumCarePhysician?: boolean;
}

export const AllFilterToggleSwitchGroup = ({
  sectionType,
  selectedFilters,
  setSelectedFilters,
  providerFilters = [],
  suppressPremiumCarePhysician = false,
}: Props) => {
  const { t } = useTranslation();

  const tierBenefits = useLagoon('tier-benefit')();
  const getTokenOrUrlParams = useGetTokenOrUrlParams();
  const resultsPageName = getTokenOrUrlParams?.search;

  const [
    tierOneFilterCompareFeatureFlag,
    preferredProviderToggleFilterFlag,
    enableExpressAccessProviderFilter,
    enableEmployeeAssistanceProgramAcceptedFilter,
    enableBhVirtualCareFilterV2,
    enableLicensedInPediatricsFilter,
    showTierOneFlag,
    enableBccFilter,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.TIER_ONE_FILTER_COMPARE,
    ConstantsLagoon.FEATURE_FLAGS
      .ENABLE_CUSTOM_PREFFERED_PROVIDER_TOGGLE_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_EXPRESS_ACCESS_PROVIDER_FILTER,
    ConstantsLagoon.FEATURE_FLAGS
      .ENABLE_EMPLOYEE_ASSISTANCE_PROGRAM_ACCEPTED_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_VIRTUAL_CARE_FILTER_V2,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LICENSED_IN_PEDIATRICS_FILTER,
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[
      Constants.CHIPS_CATEGORIES.PRIMARY_CARE
    ].TIER_ONE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BCC_FILTER,
  ]);
  const customPreferredPoliciesData: CustomPreferredPolicy[] = useLagoon(
    'custom-preferred-policies'
  )();

  const allConfigs = useLagoon('config');
  const pediatricSpecialtyCodesRaw = getLagoonConfigValue(
    allConfigs,
    ConstantsLagoon.CONFIG.PEDIATRIC_SPECIALTY_CODES
  );

  const pediatricSpecialtyCodes = parseLagoonConfigArray(
    pediatricSpecialtyCodesRaw
  );

  const suppressPremiumCare: boolean = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );

  const { suppressForNiceMembers: suppressPremiumCareForNiceMembers } =
    useSuppressNiceMembers();

  const isTier1Plan = useStore(StoreKeys.TIER1_PLAN_FLAG);

  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();
  const providerSchedulingToggleLabel = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.PRIORITY_SCHEDULING_TOGGLE_MESSAGE,
  })?.message;

  const providerSchedulingSelectedChipLabel = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.PRIORITY_SCHEDULING_TITLE,
  })?.message;
  const coverageType = useCoverageType();
  const currentMember = getCurrentMember();
  const {
    lineOfBusiness,
    population,
    isIFPTieredPlan,
    policyNumber,
    reciprocityId,
  } = currentMember || {};
  const isCns = isCnsMember(lineOfBusiness);
  const notMNRMember = isNotMNRMember(population);
  const { portalData } = useContext(PortalContext);
  const portalLockEAPfilter = portalData?.portalConfig?.lockEapFilter;
  const portalShowEapFilter = portalData?.portalConfig?.showEapFilter;
  const { providerType = '' } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      providerType: state.typeaheadSearchStore.providerType,
    }))
  );

  const policies = customPreferredPoliciesData?.filter(
    (policy) =>
      removeLeadingZeroes(policy?.policyId) ===
        removeLeadingZeroes(policyNumber) &&
      policy?.reciprocityId === reciprocityId
  );

  const preferredProviderPolicyAvailable = policies?.length > 0;

  const customPreferredPolicyBadgeLabel = preferredProviderPolicyAvailable
    ? policies?.[0].badgeLabel
    : '';
  const isVirtualCareQuickCareSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const suppressVirtualCare =
    isVirtualCareQuickCareSuppressed ||
    (coverageType !== ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
      isVirtualCareUnetRailroadSuppressed);
  const showTier1FilterToggle = () => {
    let showTier1Filter = false;

    if (!showTierOneFlag) {
      return false;
    }

    if (isCns) {
      return false;
    }
    // for non-MNR members, if TIER_ONE_FILTER_COMPARE is ON, suppress Tier1 identifier based on 'isTier1Plan' field from snackCard api response
    // and for MNR members, suppress Tier1 identifier based on plan configuration in lagoon
    if (isMNRMember(lineOfBusiness)) {
      showTier1Filter = validateMnrTieredBenefitEligibility(
        currentMember,
        tierBenefits,
        providerType
      );
    } else {
      showTier1Filter = isIFPTieredPlan ? false : !!isTier1Plan;
    }
    // If the feature_flag TIER_ONE_FILTER_COMPARE is ON, then suppress Tier1
    // identifier based on 'isTier1Plan' field from snackCard api response
    return tierOneFilterCompareFeatureFlag ? showTier1Filter : true;
  };
  const isVirtualCareSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_EMPIRE
  );

  const isVirtualCareToggleSwitchSuppressedForProviderGroup =
    returnSuppressFlag(
      ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
        .VIRTUAL_CARE_TOGGLE_SWITCH_PROVIDER_GROUP
    );

  const portalShowVirtualVisits = portalShouldShowVirtualVisits();

  // Check for enableBccSearch URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const enableBccSearchFromUrl = urlParams.get('enableBccSearch') === 'true';

  const filters = [
    {
      label: customPreferredPolicyBadgeLabel,
      model: FilterCollectionModel.Preferred_Providers,
      allFilterDrawerOrder: 1,
      sections: {
        [MEDICAL]: [PROVIDER, PROVIDER_GROUPS, FACILITY],
        [BEHAVIORAL_HEALTH]: [PROVIDER],
        [VISION]: [],
      },
      dnt_label: customPreferredPolicyBadgeLabel,
      show:
        preferredProviderToggleFilterFlag &&
        preferredProviderPolicyAvailable &&
        customPreferredPolicyBadgeLabel,
    },
    {
      label: t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.EXPRESS_ACCESS_PROVIDER'),
      allFilterDrawerOrder: 5,
      model: FilterCollectionModel.EXPRESS_ACCESS_PROVIDER_INDICATOR,
      sections: {
        [MEDICAL]: [],
        [BEHAVIORAL_HEALTH]: [PROVIDER],
        [VISION]: [],
      },
      dnt_label:
        translationsEnJson.CONTENT_FILTERS.ALL_FILTERS_DRAWER
          .EXPRESS_ACCESS_PROVIDER,
      show: enableExpressAccessProviderFilter,
    },
    {
      label: t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.EAP_OFFERED'),
      allFilterDrawerOrder: 5,
      model: FilterCollectionModel.EAP_OFFERED,
      sections: {
        [MEDICAL]: [],
        [BEHAVIORAL_HEALTH]: notMNRMember ? [PROVIDER, PROVIDER_GROUPS] : [],
        [VISION]: [],
      },
      dnt_label:
        translationsEnJson.CONTENT_FILTERS.ALL_FILTERS_DRAWER.EAP_OFFERED,
      show:
        enableEmployeeAssistanceProgramAcceptedFilter &&
        (portalShowEapFilter === true || isMemberEAPEligible()),
    },
    {
      label: t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.TIER_INDICATOR'),
      model: FilterCollectionModel.TIERED_PROVIDER,
      allFilterDrawerOrder: 1,
      sections: {
        [MEDICAL]: [PROVIDER, FACILITY, PROVIDER_GROUPS],
        [BEHAVIORAL_HEALTH]: [],
        [VISION]: [],
      },
      dnt_label:
        translationsEnJson.CONTENT_FILTERS.ALL_FILTERS_DRAWER.TIER_INDICATOR,
      show: showTier1FilterToggle(),
    },
    {
      label: t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.PREMIUM_CARE_PHYSICIAN'),
      model: FilterCollectionModel.PREMIUM_CARE_PHYSICIAN,
      allFilterDrawerOrder: 2,
      sections: {
        [MEDICAL]: [PROVIDER],
        [BEHAVIORAL_HEALTH]: [],
        [VISION]: [],
      },
      dnt_label:
        translationsEnJson.CONTENT_FILTERS.ALL_FILTERS_DRAWER
          .PREMIUM_CARE_PHYSICIAN,
      show:
        !(suppressPremiumCare || suppressPremiumCareForNiceMembers) &&
        !suppressPremiumCarePhysician,
    },
    {
      label: t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.PLATINUM_PROVIDER'),
      model: FilterCollectionModel.PLATINUM_PROVIDER_INDICATOR,
      allFilterDrawerOrder: 4,
      sections: {
        [MEDICAL]: [],
        [BEHAVIORAL_HEALTH]: [FACILITY],
        [VISION]: [],
      },
      dnt_label:
        translationsEnJson.CONTENT_FILTERS.ALL_FILTERS_DRAWER.PLATINUM_PROVIDER,
      show: !isCns,
    },
    {
      label: providerSchedulingToggleLabel,
      model: FilterCollectionModel.PRIORITY_SCHEDULING,
      allFilterDrawerOrder: 1,
      sections: {
        [MEDICAL]: [],
        [BEHAVIORAL_HEALTH]: [PROVIDER],
        [VISION]: [],
      },
      dnt_label: providerSchedulingToggleLabel,
      show: enableBccFilter && enableBccSearchFromUrl,
    },
    {
      label: t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.VIRTUAL_CARE'),
      model: FilterCollectionModel.VIRTUAL_CARE,
      allFilterDrawerOrder: 3,
      sections: {
        [MEDICAL]: [PROVIDER, PROVIDER_GROUPS],
        [BEHAVIORAL_HEALTH]: enableBhVirtualCareFilterV2
          ? [PROVIDER_GROUPS]
          : [PROVIDER, PROVIDER_GROUPS],
        [VISION]: [],
      },
      dnt_label:
        translationsEnJson.CONTENT_FILTERS.ALL_FILTERS_DRAWER.VIRTUAL_CARE,
      show:
        !(
          resultsPageName ===
            Constants.TOP_PRIMARY_CARD_MEDICAL_GROUPS.HEADING &&
          isVirtualCareToggleSwitchSuppressedForProviderGroup
        ) &&
        !isVirtualCareSuppressed &&
        portalShowVirtualVisits &&
        !suppressVirtualCare,
    },
    {
      label: t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.LICENSED_IN_PEDIATRICS'),
      model: FilterCollectionModel.LICENSED_IN_PEDIATRICS,
      allFilterDrawerOrder: 6,
      sections: {
        [MEDICAL]: [PROVIDER, PROVIDER_GROUPS],
        [BEHAVIORAL_HEALTH]: [],
        [VISION]: [],
      },
      dnt_label:
        translationsEnJson.CONTENT_FILTERS.ALL_FILTERS_DRAWER
          .LICENSED_IN_PEDIATRICS,
      show: enableLicensedInPediatricsFilter,
    },
  ];

  const toggleFilters = filters.filter(
    ({ sections, show = true }) =>
      sections[coverageType]?.includes(sectionType) && show
  );

  // Virtual Care & premium care filter selected fropm chipFilter will be an array.
  const toggleStatusFromSelectedFilters = (
    model:
      | FilterCollectionModel.VIRTUAL_CARE
      | FilterCollectionModel.PREMIUM_CARE_PHYSICIAN
  ) =>
    Array.isArray(selectedFilters[model])
      ? selectedFilters[model]?.[0]?.value || false
      : selectedFilters[model]?.value || false;

  let toggleStatus = {};
  let toggleDisabledStatus = {};
  let toggleDisabledByBccStatus = {};

  // Check if Priority Scheduling is active
  const isBccActive = isPrioritySchedulingActive(selectedFilters);

  // Auto-select "Next 7 Days" when Priority Scheduling becomes active
  useEffect(() => {
    if (isBccActive) {
      const filtersWithBccLogic = applyBccAutoSelectLogic(
        selectedFilters,
        providerFilters,
        true
      );

      // Only update if the filters actually changed
      const currentUpcomingAvailability =
        selectedFilters[FilterCollectionModel.UPCOMING_AVAILABILITY];
      const newUpcomingAvailability =
        filtersWithBccLogic[FilterCollectionModel.UPCOMING_AVAILABILITY];

      const hasChanged =
        JSON.stringify(currentUpcomingAvailability) !==
        JSON.stringify(newUpcomingAvailability);

      if (hasChanged) {
        setSelectedFilters(
          {
            ...selectedFilters,
            [FilterCollectionModel.UPCOMING_AVAILABILITY]:
              newUpcomingAvailability,
          },
          FilterCollectionModel.PRIORITY_SCHEDULING
        );
      }
    }
  }, [isBccActive]);

  toggleFilters.forEach((x) => {
    let toggleOn: boolean;
    let toggleDisabled: boolean = false;

    // Disable EAP, Virtual Care, and Upcoming Availability when Priority Scheduling is active
    const shouldDisableForBcc =
      isBccActive &&
      (x.model === FilterCollectionModel.EAP_OFFERED ||
        x.model === FilterCollectionModel.VIRTUAL_CARE ||
        x.model === FilterCollectionModel.UPCOMING_AVAILABILITY);

    if (
      enableEmployeeAssistanceProgramAcceptedFilter &&
      (x.label.includes(EAP_LABEL) || x.label.includes(EAP_LABEL_ES))
    ) {
      toggleOn = eapPresent(selectedFilters);
      toggleDisabled = portalLockEAPfilter || shouldDisableForBcc;
    } else if (
      x.model === FilterCollectionModel.VIRTUAL_CARE ||
      x.model === FilterCollectionModel.PREMIUM_CARE_PHYSICIAN
    ) {
      toggleOn = toggleStatusFromSelectedFilters(x.model);
      // Disable Virtual Care when BCC is active
      toggleDisabled =
        x.model === FilterCollectionModel.VIRTUAL_CARE && shouldDisableForBcc;
    } else if (x.model === FilterCollectionModel.UPCOMING_AVAILABILITY) {
      toggleOn = selectedFilters[x.model]?.value || false;
      // Disable Upcoming Availability when BCC is active
      toggleDisabled = shouldDisableForBcc;
    } else {
      toggleOn = selectedFilters[x.model]?.value || false;
    }
    toggleStatus = { ...toggleStatus, [x.label]: toggleOn };
    toggleDisabledStatus = {
      ...toggleDisabledStatus,
      [x.label]: toggleDisabled,
    };
    toggleDisabledByBccStatus = {
      ...toggleDisabledByBccStatus,
      [x.label]: shouldDisableForBcc,
    };
  });

  const onToggleChange = (e, model, dnt_label, label) => {
    if (toggleDisabledStatus[label]) {
      return;
    }
    adobeLinkTrackEvent({
      name: `${dnt_label}:${e.target.checked ? 'check' : 'uncheck'}`,
      location: `modal:all filters drawer`,
      type: 'checkbox',
    });

    if (
      enableEmployeeAssistanceProgramAcceptedFilter &&
      model === FilterCollectionModel.EAP_OFFERED
    ) {
      const EAPOption = getEAPOptionDetails(providerFilters);

      const updatedValue = e.target.checked
        ? [...(selectedFilters?.AreaOfExpertise || []), EAPOption]
        : selectedFilters.AreaOfExpertise?.filter(
            (option) => option?.dnt_label !== EAP_OPTION_LABEL
          );

      setSelectedFilters({
        ...selectedFilters,
        [model]: updatedValue,
      });
    } else if (model === FilterCollectionModel.VIRTUAL_CARE) {
      setSelectedFilters({
        ...selectedFilters,
        [model]: {
          label: t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.OFFERS_VIRTUAL_CARE'),
          value: e.target.checked,
          dnt_label,
        },
      });
    } else if (model === FilterCollectionModel.PREMIUM_CARE_PHYSICIAN) {
      setSelectedFilters({
        ...selectedFilters,
        [model]: {
          label: t('CONTENT_FILTERS.ALL_FILTERS_DRAWER.PREMIUM_CARE_PHYSICIAN'),
          value: e.target.checked,
          dnt_label,
        },
      });
    } else if (model === FilterCollectionModel.PRIORITY_SCHEDULING) {
      const updatedFilters = {
        ...selectedFilters,
        [model]: {
          label: providerSchedulingSelectedChipLabel,
          value: e.target.checked,
          dnt_label,
        },
      };

      // Apply BCC Filter Auto-Select logic
      const filtersWithBccLogic = applyBccAutoSelectLogic(
        updatedFilters,
        providerFilters,
        e.target.checked
      );

      setSelectedFilters(
        filtersWithBccLogic,
        FilterCollectionModel.PRIORITY_SCHEDULING
      );
    } else if (model === FilterCollectionModel.LICENSED_IN_PEDIATRICS) {
      const currentSpecialtyCodes =
        selectedFilters[FilterCollectionModel.SPECIALTY_CODE] || [];

      if (e.target.checked) {
        // When toggling ON: Remove all non-pediatric specialty codes
        const filteredSpecialtyCodes = currentSpecialtyCodes.filter((option) =>
          pediatricSpecialtyCodes.includes(option.value)
        );

        setSelectedFilters({
          ...selectedFilters,
          [FilterCollectionModel.SPECIALTY_CODE]: filteredSpecialtyCodes,
          [model]: {
            label,
            value: e.target.checked,
            dnt_label,
          },
        });
      } else {
        setSelectedFilters({
          ...selectedFilters,
          [model]: {
            label,
            value: e.target.checked,
            dnt_label,
          },
        });
      }
    } else {
      setSelectedFilters({
        ...selectedFilters,
        [model]: {
          label,
          value: e.target.checked,
          dnt_label,
        },
      });
    }

    if (toggleStatus.hasOwnProperty(label)) {
      toggleStatus[label] = !toggleStatus[label];
    }
  };

  toggleFilters.sort((a, b) => a.allFilterDrawerOrder - b.allFilterDrawerOrder);

  return (
    <div>
      {toggleFilters.length ? (
        <Flex
          css={{
            '&.abyss-flex-root': {
              width: '100%',
              display: 'flex',
              gap: '24px',
              flexDirection: 'column',
              paddingBottom: '$lg',
              borderBottom: '1px solid $core.color.neutral.30',
              paddingLeft:
                'var(--abyss-space-modalDialog-spacing-padding-horizontal-header)',
              paddingRight:
                'var(--abyss-space-modalDialog-spacing-padding-horizontal-header)',
              paddingTop:
                'var(--abyss-space-drawer-spacing-gap-vertical-content)',
            },
            '.abyss-toggle-root': {
              margin: 0,
            },
            '.abyss-toggle-label': {
              lineHeight: '20px',
            },
          }}
        >
          {toggleFilters.map(({ model, label, dnt_label }) => (
            <div key={label}>
              {model === FilterCollectionModel.EAP_OFFERED &&
              toggleDisabledByBccStatus[label] ? (
                <Tooltip
                  content={String(
                    t('CONTENT_FILTERS.PRIORITY_SCHEDULING_DISABLED_TOOLTIP') ||
                      ''
                  )}
                >
                  <div
                    data-testid={`${label
                      .replace(/ /g, '-')
                      .toLowerCase()}-priority-scheduling-disabled-tooltip`}
                  >
                    <Toggle
                      data-auto-testid="switch"
                      data-testid={`${model}_test`}
                      isChecked={toggleStatus[label]}
                      isDisabled={toggleDisabledStatus[label]}
                      label={label}
                      onChange={(e) =>
                        onToggleChange(e, model, dnt_label, label)
                      }
                    />
                  </div>
                </Tooltip>
              ) : (
                <Toggle
                  data-auto-testid="switch"
                  data-testid={`${model}_test`}
                  isChecked={toggleStatus[label]}
                  isDisabled={toggleDisabledStatus[label]}
                  label={label}
                  onChange={(e) => onToggleChange(e, model, dnt_label, label)}
                />
              )}
              {(model === FilterCollectionModel.PRIORITY_SCHEDULING ||
                model ===
                  FilterCollectionModel.PLATINUM_PROVIDER_INDICATOR) && (
                <CheckBoxPopover filterModel={model} />
              )}
            </div>
          ))}
        </Flex>
      ) : null}
    </div>
  );
};
